/**
 * useDraftGate
 *
 * Trade Analyser needs real rosters to compare, so both /trade/analyse and
 * /trade/radar have to block on "has this league's draft finished" before
 * rendering anything roster-shaped. That check used to live once, in the
 * single TradeAnalyser.jsx file; splitting into two pages meant either
 * copy-pasting it or lifting it here.
 *
 * Also fixes a latent bug from the single-file version: the gate used to be
 * `!!league && !league.draftCompleted`. If the league fetch REJECTED,
 * `league` stayed null, so the gate read `false` — pending-state was
 * skipped — and whatever rendered next did so against an empty roster.
 * `draftPending` here defaults to true (blocked) unless the league loaded
 * AND explicitly reports draftCompleted === true, so a failed fetch reads
 * as "not ready" rather than "go ahead".
 */
import { useEffect, useState } from 'react'
import { leaguesApi } from '../services/api'

export function useDraftGate(id) {
  const [league, setLeague] = useState(null)
  // loading + leagueError live in one object so the effect only ever makes
  // one setState call before the fetch settles, not two back to back.
  const [status, setStatus] = useState({ loading: true, error: false })

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setStatus({ loading: true, error: false })
      try {
        const res = await leaguesApi.get(id)
        if (!cancelled) setLeague(res.data.league)
      } catch {
        if (!cancelled) setStatus(s => ({ ...s, error: true }))
      } finally {
        if (!cancelled) setStatus(s => ({ ...s, loading: false }))
      }
    }
    load()
    return () => { cancelled = true }
  }, [id])

  const draftPending = !(league && league.draftCompleted === true)
  const sport = (league?.sport || 'nba').toLowerCase()

  return { league, loading: status.loading, leagueError: status.error, draftPending, sport }
}
